import React, { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SalesCustomerAverage = ({ data }) => {
  const avg = data?.customerAverageSpending;
  if (!avg) return null;

  const [viewMode, setViewMode] = useState("perHour");

  const chartConfig = (() => {
    if (viewMode === "perDay") {
      const labels = avg.current.perDay.map((d) =>
        dayjs.tz(d.date, "Asia/Manila").format("MMM D, YYYY")
      );
      const values = avg.current.perDay.map((d) => d.avg);
      return {
        labels,
        datasets: [
          {
            label: "Avg Spend per Order - Per Day",
            data: values,
            backgroundColor: "#4CAF50",
          },
        ],
      };
    }

    if (viewMode === "perHour") {
      const labels = avg.current.perHour.map((d) => {
        const hour = d.hour;
        const formatted = dayjs()
          .hour(hour)
          .minute(0)
          .tz("Asia/Manila")
          .format("h A");
        return formatted;
      });
      const values = avg.current.perHour.map((d) => d.avg);
      return {
        labels,
        datasets: [
          {
            label: "Avg Spend per Order - Per Hour",
            data: values,
            backgroundColor: "#2196F3",
          },
        ],
      };
    }

    if (viewMode === "perRange") {
      return {
        labels: ["Previous Range", "Current Range"],
        datasets: [
          {
            label: "Avg Spend per Order",
            data: [avg.previous.rangeAvg || 0, avg.current.rangeAvg || 0],
            backgroundColor: ["#9E9E9E", "#FF9800"],
          },
        ],
      };
    }

    return { labels: [], datasets: [] };
  })();

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text:
          viewMode === "perDay"
            ? "Average Customer Spend per Day"
            : viewMode === "perHour"
            ? "Average Customer Spend per Hour"
            : "Average Customer Spend (Date Range)",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw || 0;
            return `${context.dataset.label}: ₱${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (val) => `₱${val.toLocaleString()}`,
        },
      },
    },
  };

  return (
    <Paper sx={{ mt: 3, p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Average Customer Spend</Typography>
        <FormControl size="small">
          <InputLabel>View</InputLabel>
          <Select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            label="View"
          >
            <MenuItem value="perHour">Per Hour</MenuItem>
            <MenuItem value="perDay">Per Day</MenuItem>
            <MenuItem value="perRange">Per Date Range</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Bar data={chartConfig} options={chartOptions} />
      </Box>
    </Paper>
  );
};

export default SalesCustomerAverage;
